/**
 * agent_mem.ts — visited-memory variant of the GRU DRQN inference agent.
 *
 * This is the SECOND model in the demo, alongside the baseline LitertAgent.
 * It loads gru_mem.tflite (produced by scratchbooks/.../train/_train_visited_memory.py
 * from the validated _proto_visited_memory.py:GRUDRQNMemory prototype).
 *
 * Architecture: identical to the baseline GRU cell, PLUS a third input tensor
 * `m_in [1, GRID*GRID]` — the flattened visited bitmap. A Dense(H)+tanh
 * projection on m_in produces m_proj, added INSIDE the candidate's
 * pre-activation:
 *
 *     m_proj = tanh(W_Mm·m_in + b_M)
 *     h_cand = tanh(W_xh·x + W_hh·(r⊙h_in) + b_h + m_proj)
 *
 * Pure-Python A/B at 20×20, 2800 episodes, 3 seeds: +19.4% matched-pair
 * ratio (mem/baseline), memory wins on 3/3 seeds, memory's worst seed
 * beats baseline's best.
 *
 * THE ONLY DIFFERENCE from LitertAgent is the input contract:
 *   - assembleInputs() reads env.shadow.confident (the visited bitmap
 *     maintained by Env.step → ShadowBelief.markVisited) and inserts a
 *     third Tensor in TFLite's alphabetical-by-name order: [h_in, m_in, x].
 *
 * The rest of stepAsync (output resolution, can_move mask, LoopGate,
 * taboo mask, post-step state threading, tensor cleanup) is inherited
 * unchanged from LitertAgent.
 *
 * Implementation note — we don't keep a TS-side accumulator: env.shadow.confident
 * IS the accumulator. The Env clears it on reset (ShadowBelief.reset fills
 * confident with 0 at environment.ts:328-332), and Env.step writes a 1 into
 * the agent's current cell every tick via markVisited at environment.ts:568.
 * We just copy Uint8→Float32 once per tick and ship it to the model.
 */

import { Tensor } from '@litertjs/core';
import { Env, GRID, N_ACTIONS, D_OBS } from './environment';
import { LitertAgent } from './agent';

const GRID_SQ = GRID * GRID;
const HIDDEN = 48;
const D_IN = N_ACTIONS + D_OBS; // 8 + 33 = 41 — identical to baseline

export class LitertMemAgent extends LitertAgent {
  /** Build the per-tick input tensor list in CANONICAL order
   *  [h_in, m_in, x]. stepAsync permutes this into the model's native
   *  input-details order at runtime; the load-time resolver in
   *  LitertAgent.load() matches inputShapes() against getInputDetails().
   *
   *  We read the visited bitmap off env.shadow.confident (Uint8Array(GRID_SQ))
   *  and widen to Float32 in one pass. The Tensor constructor copies the
   *  data; the intermediate mArr can go out of scope after the Tensor is
   *  built. The returned array is freed by the parent's cleanup loop
   *  (which iterates `inputs`). */
  protected override assembleInputs(env: Env, _obs: Float64Array, xArr: Float32Array): Tensor[] {
    const mArr = new Float32Array(GRID_SQ);
    const conf = env.shadow.confident;
    for (let i = 0; i < GRID_SQ; i++) mArr[i] = conf[i];

    const xTensor = new Tensor(xArr, [1, D_IN]);
    const hInTensor = new Tensor(new Float32Array(this.h), [1, HIDDEN]);
    const mInTensor = new Tensor(mArr, [1, GRID_SQ]);
    // Canonical order: [h_in, m_in, x]. Matches inputShapes() below.
    return [hInTensor, mInTensor, xTensor];
  }

  /** Canonical shapes for assembleInputs(): [h_in, m_in, x].
   *  Used at load time to match each model input-details slot (which can
   *  be in any native order) to its canonical position by shape. */
  protected override inputShapes(): number[][] {
    return [[1, HIDDEN], [1, GRID_SQ], [1, D_IN]];
  }
}
